import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BarChart3, ShoppingCart, Users, DollarSign, 
  TrendingUp, Clock, CheckCircle, ArrowRight
} from 'lucide-react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { api } from '../../lib/auth';
import { formatCurrency } from '../../lib/utils';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.getAdminStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { label: 'Total Orders', value: stats.total_orders, icon: ShoppingCart, color: 'bg-blue-500/20 text-blue-400' },
    { label: 'Pending Orders', value: stats.pending_orders, icon: Clock, color: 'bg-yellow-500/20 text-yellow-400' },
    { label: 'In Progress', value: stats.in_progress, icon: TrendingUp, color: 'bg-purple-500/20 text-purple-400' },
    { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'bg-green-500/20 text-green-400' },
    { label: 'Total Revenue', value: formatCurrency(stats.total_revenue), icon: DollarSign, color: 'bg-primary/20 text-primary' },
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'bg-accent/20 text-accent' },
  ] : [];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-400">Overview of platform activity and metrics</p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8"
        >
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />
            ))
          ) : (
            statCards.map((card, i) => (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="glass rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.color.split(' ')[0]}`}>
                    <card.icon className={`w-5 h-5 ${card.color.split(' ')[1]}`} />
                  </div>
                  <span className="text-gray-400">{card.label}</span>
                </div>
                <p className="text-3xl font-heading font-bold text-white">{card.value}</p>
              </motion.div>
            ))
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <Link to="/admin/orders" data-testid="admin-orders-link">
            <div className="glass rounded-xl p-6 hover:border-primary/30 transition-colors cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-white mb-1">Manage Orders</h3>
                  <p className="text-sm text-gray-400">View and update order statuses</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-600 group-hover:text-primary transition-colors" />
              </div>
            </div>
          </Link>

          <Link to="/admin/services" data-testid="admin-services-link">
            <div className="glass rounded-xl p-6 hover:border-primary/30 transition-colors cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-white mb-1">Manage Services</h3>
                  <p className="text-sm text-gray-400">Add and edit services</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-600 group-hover:text-primary transition-colors" />
              </div>
            </div>
          </Link>

          <Link to="/marketplace" data-testid="view-marketplace-link">
            <div className="glass rounded-xl p-6 hover:border-primary/30 transition-colors cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-white mb-1">View Marketplace</h3>
                  <p className="text-sm text-gray-400">See public service listings</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-600 group-hover:text-primary transition-colors" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 glass rounded-xl p-6"
        >
          <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Platform Overview
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="p-4 rounded-lg bg-white/5">
              <h3 className="text-sm text-gray-400 mb-2">Order Distribution</h3>
              <div className="space-y-2">
                {stats && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-300">Pending</span>
                      <div className="flex items-center gap-2">
                        <div className="h-2 bg-yellow-500/50 rounded" style={{ width: `${(stats.pending_orders / Math.max(stats.total_orders, 1)) * 100}px` }} />
                        <span className="text-sm text-white">{stats.pending_orders}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-300">In Progress</span>
                      <div className="flex items-center gap-2">
                        <div className="h-2 bg-purple-500/50 rounded" style={{ width: `${(stats.in_progress / Math.max(stats.total_orders, 1)) * 100}px` }} />
                        <span className="text-sm text-white">{stats.in_progress}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-300">Completed</span>
                      <div className="flex items-center gap-2">
                        <div className="h-2 bg-green-500/50 rounded" style={{ width: `${(stats.completed / Math.max(stats.total_orders, 1)) * 100}px` }} />
                        <span className="text-sm text-white">{stats.completed}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/5">
              <h3 className="text-sm text-gray-400 mb-2">Organizations</h3>
              <p className="text-3xl font-heading font-bold text-white">
                {stats?.total_organizations || 0}
              </p>
              <p className="text-sm text-gray-500">Active organizations</p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
